/**
 * What Flow's own pages need from the sandbox admin API (vfs-service src/gateway/admin, `/sandbox/admin/api`, same
 * origin with credentials, as hooks/useSandboxAdmin.js): the organization's settings, for Connections › Organization
 * AI connections. The gateway answers only sandbox admins (403 for anyone else).
 *
 * The sign-in endpoints come from the gateway's own discovery, the one MCP clients read: the provider's metadata
 * (`/.well-known/oauth-authorization-server/sandbox/mcp`), else the protected-resource metadata's issuer with
 * Keycloak's paths.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

export const ORGANIZATION_URL = '/sandbox/admin/api/organization';
const MCP_PATH = '/sandbox/mcp';

const baseUrl = () => (config.publicBaseUrl || window.location.origin).replace(/\/$/, '');

async function readError(response, fallback) {
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') { return new Error(body.error); }
  } catch { /* not JSON */ }
  return new Error(fallback);
}

/** `GET /sandbox/admin/api/organization`: connections, model keys and AI tools (with `microsoft365Copilot`). */
export async function getOrganizationSettings() {
  const response = await getAuthorizedFetch()(ORGANIZATION_URL, { credentials: 'include', headers: { Accept: 'application/json' } });
  if (!response.ok) { throw await readError(response, `Organization settings failed (${response.status})`); }
  return response.json();
}

/** Saves the Microsoft 365 Copilot OAuth client registration ID (empty clears it); resolves to the saved settings. */
export async function saveM365RegistrationId(oauthRegistrationId) {
  const response = await getAuthorizedFetch()(ORGANIZATION_URL, {
    method: 'PUT',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ aiTools: { microsoft365Copilot: { oauthRegistrationId: String(oauthRegistrationId || '').trim() || null } } }),
  });
  if (!response.ok) { throw await readError(response, `Saving failed (${response.status})`); }
  return response.json();
}

async function getJson(url) {
  const response = await getAuthorizedFetch()(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) { throw new Error(`${url}: ${response.status}`); }
  return response.json();
}

/** `{ issuer, authorizationUrl, tokenUrl }` of the realm the sandbox signs people in with, or null when unknown. */
export async function getSignInEndpoints() {
  const base = baseUrl();
  try {
    const provider = await getJson(`${base}/.well-known/oauth-authorization-server${MCP_PATH}`);
    if (provider?.authorization_endpoint && provider?.token_endpoint) {
      return { issuer: provider.issuer || null, authorizationUrl: provider.authorization_endpoint, tokenUrl: provider.token_endpoint };
    }
  } catch { /* try the protected-resource metadata */ }
  try {
    const resource = await getJson(`${base}/.well-known/oauth-protected-resource${MCP_PATH}`);
    const issuer = String(resource?.authorization_servers?.[0] || '').replace(/\/$/, '');
    if (issuer) {
      return { issuer, authorizationUrl: `${issuer}/protocol/openid-connect/auth`, tokenUrl: `${issuer}/protocol/openid-connect/token` };
    }
  } catch { /* unknown */ }
  return null;
}
